import { min } from 'lib/array/arrayUtils'

const METASEARCH_PRICE_ACCURACY_CATEGORY = 'metasearch_price_accuracy'

type MetasearchPriceAccuracyPageType = 'spp' | 'legacy_bedbank'

interface SubjectParams {
  offerId: string
  rateType: string
  supplier?: string
  pageType: MetasearchPriceAccuracyPageType
  metasearchRoomRateId?: string
  partnerRoomRateId?: string
}

interface BedbankEventDataParams {
  offerId: string
  pageType: MetasearchPriceAccuracyPageType
  metasearchRoomRateId?: string
  rate: App.BedbankRate | undefined
  // Final rendered price (discount already applied); defaults to the rate's public total.
  price?: number
}

interface PropertyEventDataParams {
  offerId: string
  pageType: MetasearchPriceAccuracyPageType
  metasearchRoomRateId?: string
  rate: App.EnrichedPropertyRate | undefined
  // Final rendered price (discount already applied); defaults to the rate display price.
  price?: number
}

export interface MetasearchPriceAccuracyEventData {
  price: number
  subject: string
}

export function getBedbankRatePublicTotal(rate: App.BedbankRate) {
  return rate.totals.inclusive + (rate.totals.propertyFees ?? 0)
}

function getDiscountedPrice(priceBeforeDiscount: number, discountAmount = 0) {
  return Math.max(priceBeforeDiscount - discountAmount, 0)
}

export function getCheapestBedbankRateForMetasearchPriceAccuracy(
  rates: Array<App.BedbankRate>,
): App.BedbankRate | undefined {
  return min(rates, getBedbankRatePublicTotal)
}

// Bedbank is metasearch's baseline, so prefer the cheapest bedbank rate; only fall to the cheapest
// LPC rate when there's no bedbank rate to record.
export function getCheapestPropertyRatePreferringBedbank(
  rates: Array<App.EnrichedPropertyRate>,
): App.EnrichedPropertyRate | undefined {
  const bedbankRates = rates.filter(rate => rate.service.type === 'bedbank')
  return min(bedbankRates.length ? bedbankRates : rates, rate => rate.totals.displayPrice)
}

// Closest candidate to `targetPrice` in either direction, ties resolving to the lower price — how
// Google's scraper matches a price off the page. Callers price each candidate as the UI renders it.
export function pickRateClosestToPrice<Rate>(
  candidates: Array<{ rate: Rate, price: number }>,
  targetPrice: number,
): { rate: Rate, price: number } | undefined {
  let best: { rate: Rate, price: number } | undefined
  let bestDelta = Infinity

  for (const candidate of candidates) {
    const delta = Math.abs(candidate.price - targetPrice)
    if (delta < bestDelta || (delta === bestDelta && !!best && candidate.price < best.price)) {
      best = candidate
      bestDelta = delta
    }
  }

  return best
}

function encodeSubjectValue(value: string) {
  return encodeURIComponent(value)
}

function subjectPart(key: string, value: string | undefined) {
  if (!value) return undefined
  return `${key}:${encodeSubjectValue(value)}`
}

function buildMetasearchPriceAccuracySubject(params: SubjectParams) {
  return [
    subjectPart('offerId', params.offerId),
    subjectPart('rateType', params.rateType),
    subjectPart('supplier', params.supplier),
    subjectPart('pageType', params.pageType),
    subjectPart('metasearchRoomRateId', params.metasearchRoomRateId),
    subjectPart('partnerRoomRateId', params.partnerRoomRateId),
  ].filter((part): part is string => !!part).join('|')
}

export function getMetasearchPriceAccuracyEvent(
  eventData: MetasearchPriceAccuracyEventData,
) {
  return {
    subject: eventData.subject,
    action: eventData.price.toString(),
    category: METASEARCH_PRICE_ACCURACY_CATEGORY,
    type: 'operational' as const,
  }
}

export function getBedbankMetasearchPriceAccuracyEventData({
  offerId,
  pageType,
  metasearchRoomRateId,
  rate,
  price,
}: BedbankEventDataParams): MetasearchPriceAccuracyEventData | undefined {
  if (!rate) return undefined

  return {
    price: price ?? getBedbankRatePublicTotal(rate),
    subject: buildMetasearchPriceAccuracySubject({
      offerId,
      rateType: 'bedbank',
      supplier: rate.supplierCode,
      pageType,
      metasearchRoomRateId,
      partnerRoomRateId: rate.id,
    }),
  }
}

interface SelectCandidateParams<Rate> {
  candidates: Array<Rate>
  getPrice: (rate: Rate) => number
  isDiscountEligible: (rate: Rate) => boolean
  discountAmount: number
  priceTotal: number | undefined
  fallbackRate: Rate | undefined
}

// The discount is applied only to the eligible rate. With a `priceTotal` we record the closest
// on-page rate to it; without one we record `fallbackRate`.
export function selectMetasearchPriceAccuracyCandidate<Rate>({
  candidates,
  getPrice,
  isDiscountEligible,
  discountAmount,
  priceTotal,
  fallbackRate,
}: SelectCandidateParams<Rate>): { rate: Rate, price: number } | undefined {
  const effectivePrice = (rate: Rate) =>
    getDiscountedPrice(getPrice(rate), isDiscountEligible(rate) ? discountAmount : 0)

  if (priceTotal !== undefined && priceTotal > 0) {
    return pickRateClosestToPrice(
      candidates.map(rate => ({ rate, price: effectivePrice(rate) })),
      priceTotal,
    )
  }

  if (!fallbackRate) return undefined
  return { rate: fallbackRate, price: effectivePrice(fallbackRate) }
}

export function getPropertyMetasearchPriceAccuracyEventData({
  offerId,
  pageType,
  metasearchRoomRateId,
  rate,
  price,
}: PropertyEventDataParams): MetasearchPriceAccuracyEventData | undefined {
  if (!rate) return undefined

  // LPC rate/supplier ids are reservation-service identifiers we can't join on downstream, so for
  // LPC we emit only offerId + rateType + price.
  const { service } = rate

  return {
    price: price ?? rate.totals.displayPrice,
    subject: buildMetasearchPriceAccuracySubject({
      offerId,
      rateType: service.type,
      supplier: service.type === 'bedbank' ? service.supplierCode : undefined,
      pageType,
      metasearchRoomRateId,
      partnerRoomRateId: service.type === 'bedbank' ? service.rateId : undefined,
    }),
  }
}
